import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Droplet, Boxes, Container, Layers, Weight, Box, Send, Loader2, CheckCircle2, RotateCcw, AlertTriangle, Terminal } from "lucide-react";

const jmd = (n) => "J$" + Number(n || 0).toLocaleString();
const KG = 0.45359237;

const ALIASES = [
  [/355\s?ml|355/i, "355ml"], [/500\s?ml|500/i, "500ml"],
  [/1\.5\s?l|1500\s?ml|1\.5/i, "1.5L"], [/5\s?gal(lon)?/i, "5 gallon"], [/5\s?l\b|5l/i, "5L"],
];
function matchSize(text) { for (const [re, k] of ALIASES) if (re.test(text)) return k; return null; }

function parseCommand(text, order, qty) {
  const t = text.trim().toLowerCase();
  const next = { ...qty };
  let hit = false;
  if (/^(remove|take out|delete|drop)\b/.test(t)) { const s = matchSize(t); if (s) { next[s] = 0; hit = true; } return { next, hit }; }
  const s = matchSize(t);
  const stripped = t.replace(/1\.5\s?l|1500\s?ml|355\s?ml|500\s?ml|5\s?gal(?:lon)?|5\s?l\b/gi, " ");
  const num = (stripped.match(/(\d[\d,]*)/) || [])[1];
  const n = num ? parseInt(num.replace(/,/g, ""), 10) : null;
  if (s && n != null) {
    if (/^(add|another|plus)\b/.test(t)) next[s] = (parseInt(next[s] || 0, 10) || 0) + n;
    else next[s] = n; // set / make / change / "500ml: 250"
    hit = true;
  }
  return { next, hit };
}

function computeShipment(cfg, qty) {
  const { products, order, pallet_consolidation: consol } = cfg;
  const lines = []; let cases = 0, weight = 0, sumEquiv = 0, sumCeil = 0, cargoVol = 0, totalPrice = 0;
  const incomplete = []; let volKnown = true, maxPh = 0, heightKnown = true, priced = true;
  order.forEach((k) => {
    const p = products[k]; const c = Math.max(0, parseInt(qty[k] || 0, 10) || 0);
    if (!p || c <= 0) return;
    const w = c * p.case_weight;
    const line = { size: k, label: p.label, cases: c, case_weight: p.case_weight, total_weight: w, cpp: p.cases_per_pallet, ppc: p.price_per_case };
    if (p.cases_per_pallet) { line.equiv = c / p.cases_per_pallet; line.pallets = Math.ceil(line.equiv); sumEquiv += line.equiv; sumCeil += line.pallets; }
    else { line.pallets = null; incomplete.push(p.label); }
    if (p.price_per_case) { line.lineTotal = p.price_per_case * c; totalPrice += line.lineTotal; } else priced = false;
    const cs = p.case;
    if (cs && cs.length) cargoVol += (cs.length * cs.width * cs.height) / 1728 * c; else volKnown = false;
    const ph = p.pallet && p.pallet.height;
    if (ph) maxPh = Math.max(maxPh, ph); else heightKnown = false;
    cases += c; weight += w; lines.push(line);
  });
  const has = lines.length > 0;
  const rounded = consol === "allowed" ? (sumEquiv ? Math.ceil(sumEquiv) : 0) : sumCeil;
  return { lines, cases, weight, equiv: sumEquiv, rounded, incomplete, consol, cargoVol, volKnown: volKnown && has, maxPh, heightKnown: heightKnown && has, priced: priced && has, totalPrice: priced && has ? totalPrice : null };
}

function recommend(r, cfg) {
  const containers = cfg.containers; const mode = cfg.optimization_mode || "min_containers"; const ccy = cfg.currency || "USD";
  if (r.incomplete.length) return { status: "incomplete", message: `Missing Cases-Per-Pallet for: ${r.incomplete.join(", ")}. An administrator must configure it before a container can be recommended.` };
  if (r.rounded <= 0) return { status: "empty" };
  if (r.heightKnown && r.maxPh) {
    const tallest = Math.max(...containers.map((c) => (c.internal && c.internal.height_in) || 0));
    if (r.maxPh > tallest) return { status: "infeasible", message: `A stacked pallet is ${r.maxPh} in tall, exceeding the tallest container interior (${tallest} in). Reduce stack height.` };
  }
  const fits = (c) => {
    const uv = c.usable_volume_cuft; const ch = c.internal && c.internal.height_in;
    return r.rounded <= c.pallet_capacity && r.weight <= c.payload_lb && (!r.volKnown || uv == null || r.cargoVol <= uv) && (!r.heightKnown || !r.maxPh || !ch || r.maxPh <= ch);
  };
  const build = (c, n, status) => {
    const uv = c.usable_volume_cuft;
    return { status, name: c.name, qty: n, pu: r.rounded / (n * c.pallet_capacity) * 100, wu: r.weight / (n * c.payload_lb) * 100, vu: (r.volKnown && uv) ? r.cargoVol / (n * uv) * 100 : null, cost: (c.cost != null) ? c.cost * n : null, ccy };
  };
  const singles = containers.filter(fits);
  if (singles.length) {
    let best;
    if (mode === "min_cost" && singles.some((c) => c.cost != null)) best = singles.filter((c) => c.cost != null).reduce((a, b) => a.cost <= b.cost ? a : b);
    else best = [...singles].sort((a, b) => a.pallet_capacity - b.pallet_capacity || a.payload_lb - b.payload_lb)[0];
    return build(best, 1, "single");
  }
  const cand = containers.map((c) => {
    const uv = c.usable_volume_cuft;
    let n = Math.max(Math.ceil(r.rounded / c.pallet_capacity), Math.ceil(r.weight / c.payload_lb));
    if (r.volKnown && uv) n = Math.max(n, Math.ceil(r.cargoVol / uv));
    return { c, n: Math.max(n, 1) };
  });
  const costAvail = containers.some((c) => c.cost != null);
  let pick;
  if (mode === "min_cost" && costAvail) pick = cand.filter((x) => x.c.cost != null).reduce((a, b) => (a.c.cost * a.n <= b.c.cost * b.n ? a : b));
  else if (mode === "max_utilization") pick = cand.reduce((a, b) => (a.n * a.c.pallet_capacity <= b.n * b.c.pallet_capacity ? a : b));
  else if (mode === "balanced") pick = cand.reduce((a, b) => (a.n !== b.n ? (a.n < b.n ? a : b) : (((a.c.cost || 1e12) * a.n) <= ((b.c.cost || 1e12) * b.n) ? a : b)));
  else pick = cand.reduce((a, b) => a.n <= b.n ? a : b);
  return build(pick.c, pick.n, "multi");
}

function Bar({ label, pct }) {
  const v = Math.min(100, Math.round(pct * 10) / 10);
  return (
    <div>
      <div className="flex justify-between text-[11px] text-white/60 mb-1"><span>{label}</span><span>{v}%</span></div>
      <div className="h-2 rounded-full bg-white/15 overflow-hidden"><div className="h-full bg-emerald transition-all" style={{ width: `${v}%` }} /></div>
    </div>
  );
}

export function WholesaleQuote() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [qty, setQty] = useState({});
  const [unit, setUnit] = useState("lb");
  const [cmd, setCmd] = useState("");
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", phone: "", company: "", country: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { api.get("/quote/config").then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  useEffect(() => { setForm((f) => ({ ...f, name: f.name || user?.name || "", email: f.email || user?.email || "" })); }, [user]);

  const r = useMemo(() => (cfg ? computeShipment(cfg, qty) : null), [cfg, qty]);
  const rec = useMemo(() => (cfg && r ? recommend(r, cfg) : null), [cfg, r]);

  if (!cfg) return <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-emerald" size={26} /></div>;

  const wt = (lb) => unit === "kg" ? `${(lb * KG).toLocaleString(undefined, { maximumFractionDigits: 1 })} kg` : `${lb.toLocaleString()} lb`;

  const runCmd = () => {
    if (!cmd.trim()) return;
    const { next, hit } = parseCommand(cmd, cfg.order, qty);
    if (hit) { setQty(next); setCmd(""); } else toast.error("Couldn't understand that — try e.g. \"500ml: 250\" or \"remove 5L\"");
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Please enter your name");
    if (!r || r.lines.length === 0) return toast.error("Enter case quantities for at least one product");
    setBusy(true);
    try {
      const items = cfg.order.map((k) => ({ size: k, cases: parseInt(qty[k] || 0, 10) || 0 })).filter((i) => i.cases > 0);
      const { data } = await api.post("/quote", { ...form, items });
      setDone(data); toast.success(`Quote ${data.ref} sent!`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit quote"); }
    setBusy(false);
  };

  if (done) {
    const rc = done.recommendation;
    return (
      <div className="bg-card rounded-2xl border border-border p-8 text-center" data-testid="quote-done">
        <CheckCircle2 size={56} className="mx-auto text-emerald mb-3" />
        <h3 className="font-display font-bold text-2xl">Quote request received!</h3>
        <p className="text-muted-foreground mt-2">Reference <span className="font-mono-sn font-bold text-ink">{done.ref}</span> — our export team will follow up.</p>
        <div className="mt-4 inline-flex flex-wrap justify-center gap-4 text-sm font-semibold">
          <span>{done.summary.total_cases} cases</span><span className="text-muted-foreground">·</span>
          <span>{wt(done.summary.total_weight_lb)}</span><span className="text-muted-foreground">·</span>
          <span>{done.summary.rounded_pallets} pallets</span>
          {rc?.name && <><span className="text-muted-foreground">·</span><span className="text-emerald">{rc.qty} × {rc.name}</span></>}
        </div>
        <button onClick={() => { setQty({}); setDone(null); }} className="mt-6 inline-flex items-center gap-2 bg-ink text-white rounded-full px-6 py-3 text-sm font-semibold" data-testid="quote-new-btn"><RotateCcw size={15} /> New calculation</button>
      </div>
    );
  }

  const empty = r.lines.length === 0;
  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="wholesale-quote">
      <div className="lg:col-span-3 space-y-4">
        {/* Natural language */}
        <div className="bg-card rounded-2xl border border-border p-3 flex items-center gap-2">
          <Terminal size={16} className="text-emerald shrink-0 ml-1" />
          <input value={cmd} onChange={(e) => setCmd(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runCmd()} placeholder='Type e.g. "500ml: 250", "add 100 cases of 355ml", "remove 5L"' className="flex-1 bg-transparent text-sm outline-none" data-testid="quote-command" />
          <button onClick={runCmd} className="text-xs font-bold bg-ink text-white rounded-lg px-3 py-1.5" data-testid="quote-command-run">Apply</button>
        </div>

        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <Droplet size={18} className="text-emerald" /><h3 className="font-display font-bold">Cases to export</h3>
            <button onClick={() => setUnit(unit === "lb" ? "kg" : "lb")} className="ml-auto text-xs font-bold border border-border rounded-full px-3 py-1" data-testid="quote-unit-toggle">Show {unit === "lb" ? "kg" : "lb"}</button>
          </div>
          <div className="divide-y divide-border">
            {cfg.order.map((k) => {
              const p = cfg.products[k]; const line = r.lines.find((l) => l.size === k);
              const noCpp = !p.cases_per_pallet;
              return (
                <div key={k} className="flex items-center gap-4 px-5 py-4" data-testid={`quote-row-${k}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold flex items-center gap-2">{p.label}
                      {noCpp && <span className="text-[10px] font-bold text-gold bg-gold/15 px-1.5 py-0.5 rounded-full" title="Cases-per-pallet not configured"><AlertTriangle size={10} className="inline mb-0.5" /> needs config</span>}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {p.case_weight} lb/case · {noCpp ? "cases/pallet not set" : `${p.cases_per_pallet} cases/pallet`}{p.price_per_case ? ` · ${jmd(p.price_per_case)}/case` : ""}
                      {line && <> · <b className="text-ink">{wt(line.total_weight)}</b>{line.pallets != null && <> · {line.pallets} plt</>}</>}
                    </div>
                  </div>
                  <input type="number" min="0" inputMode="numeric" placeholder="0" value={qty[k] || ""} onChange={(e) => setQty({ ...qty, [k]: e.target.value })} className="w-24 rounded-xl border border-border px-3 py-2.5 text-sm text-right font-semibold" data-testid={`quote-input-${k}`} />
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-card rounded-2xl border border-border p-5 space-y-3">
          <h3 className="font-display font-bold flex items-center gap-2"><Send size={16} className="text-emerald" /> Your details</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name *" className="rounded-xl border border-border px-3 py-2.5 text-sm" data-testid="quote-name" />
            <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company" className="rounded-xl border border-border px-3 py-2.5 text-sm" data-testid="quote-company" />
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="rounded-xl border border-border px-3 py-2.5 text-sm" data-testid="quote-email" />
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone / WhatsApp" className="rounded-xl border border-border px-3 py-2.5 text-sm" data-testid="quote-phone" />
            <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Destination country" className="rounded-xl border border-border px-3 py-2.5 text-sm" data-testid="quote-country" />
          </div>
          <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} placeholder="Anything else?" className="w-full rounded-xl border border-border px-3 py-2.5 text-sm" data-testid="quote-note" />
        </div>
        <p className="text-[11px] text-muted-foreground leading-relaxed">{cfg.disclaimer}</p>
      </div>

      {/* Summary + recommendation */}
      <div className="lg:col-span-2">
        <div className="sticky top-24 rounded-2xl sn-ink-bg sn-noise relative overflow-hidden p-6 text-white" data-testid="quote-summary">
          {empty ? (
            <div className="text-center py-6 relative">
              <Container size={28} className="mx-auto text-white/40 mb-2" />
              <p className="font-display font-bold">Enter quantities</p>
              <p className="text-white/50 text-xs mt-1">Total Cases: 0 · 0 lb · 0 pallets</p>
            </div>
          ) : (
            <>
              <h3 className="font-clash font-bold text-xl flex items-center gap-2 relative"><Container size={20} className="text-emerald" /> Shipment summary</h3>
              <div className="mt-5 space-y-3 relative">
                <Stat icon={Boxes} label="Total cases" value={r.cases.toLocaleString()} />
                <Stat icon={Weight} label="Total weight" value={wt(r.weight)} />
                {r.volKnown && <Stat icon={Box} label="Cargo volume" value={`${r.cargoVol.toFixed(1)} ft³`} />}
                <Stat icon={Layers} label={`Pallets (${r.consol === "allowed" ? "consolidated" : "per product"})`} value={r.rounded} accent />
              </div>
              <div className="mt-5 pt-4 border-t border-white/15 relative">
                {(rec.status === "incomplete" || rec.status === "infeasible") ? (
                  <div className="flex items-start gap-2 text-gold text-sm"><AlertTriangle size={16} className="shrink-0 mt-0.5" /> {rec.message}</div>
                ) : (
                  <>
                    <div className="text-white/60 text-xs uppercase tracking-wider font-bold mb-1">Recommended container</div>
                    <div className="font-display font-bold text-2xl text-emerald mb-3" data-testid="quote-recommendation">{rec.qty} × {rec.name}{rec.status === "multi" ? " (multi)" : ""}</div>
                    <div className="space-y-2"><Bar label="Pallet utilisation" pct={rec.pu} /><Bar label="Payload utilisation" pct={rec.wu} />{rec.vu != null && <Bar label="Volume utilisation" pct={rec.vu} />}</div>
                    {rec.cost != null && <div className="mt-3 flex items-center justify-between"><span className="text-white/70 text-sm">Est. freight</span><span className="font-display font-bold text-lg text-emerald" data-testid="quote-freight">{rec.ccy} {Number(rec.cost).toLocaleString()}</span></div>}
                    {rec.status === "multi" && <p className="text-white/60 text-[11px] mt-2">Shipment exceeds a single container — {rec.qty} units required, subject to carrier verification.</p>}
                  </>
                )}
                {r.priced && <div className="mt-4 flex items-center justify-between"><span className="text-white/70 text-sm">Estimated total</span><span className="font-display font-bold text-2xl text-emerald" data-testid="quote-total">{jmd(r.totalPrice)}</span></div>}
              </div>
              <button onClick={submit} disabled={busy} className="mt-5 w-full flex items-center justify-center gap-2 bg-emerald text-white rounded-xl px-5 py-3 text-sm font-bold disabled:opacity-60 relative" data-testid="quote-submit">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Request quote
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-white/70 text-sm"><Icon size={15} className="text-emerald" /> {label}</span>
      <span className={`font-display font-bold ${accent ? "text-emerald text-2xl" : "text-white text-lg"}`}>{value}</span>
    </div>
  );
}
